import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { Editor } from "@tiptap/react";
import type { ChapterComment } from "../../../shared/models";
import { measureCommentToolbarPosition } from "../../utils/commentEditorMarks";
import { Button } from "../ui/button";

type ManuscriptCommentToolbarProps = {
  editor: Editor | null;
  comment: ChapterComment | null;
  canResolve: boolean;
  canDelete: boolean;
  onResolve: () => void;
  onDelete: () => void;
};

export function ManuscriptCommentToolbar({
  editor,
  comment,
  canResolve,
  canDelete,
  onResolve,
  onDelete,
}: ManuscriptCommentToolbarProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!editor || !comment) {
      setPos(null);
      return;
    }

    const update = () => {
      if (!comment) {
        setPos(null);
        return;
      }
      setPos(measureCommentToolbarPosition(editor, comment.id));
    };

    update();
    const well = document.querySelector(".manuscript-surface");
    well?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    return () => {
      well?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [editor, comment]);

  if (!editor || !comment || (!canResolve && !canDelete) || !pos) return null;

  const isResolved = Boolean(comment.resolved);

  return createPortal(
    <div
      className="fixed z-80 flex -translate-x-full -translate-y-full gap-1 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg"
      role="toolbar"
      aria-label="Comment actions"
      style={{
        top: pos.top,
        left: pos.left,
      }}
      onMouseDown={(event) => event.preventDefault()}
    >
      <Button
        type="button"
        variant="secondary"
        size="xs"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onResolve();
        }}
        hidden={!canResolve}
      >
        {isResolved ? "Reopen" : "Resolve"}
      </Button>
      <Button
        type="button"
        variant="destructive"
        size="xs"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onDelete();
        }}
        hidden={!canDelete}
      >
        Delete
      </Button>
    </div>,
    document.body
  );
}
